// Cart Progress Milestones Configuration
const MILESTONES = [
  {
    threshold: 80,
    // text ki jagah icon ka HTML (10x10)
    discount: '<img src="https://cdn.shopify.com/s/files/1/0779/6028/2437/files/delivery-truck_1_cbbd5be7-970d-4ef4-897c-35ef82f236e8.png?v=1753952344" alt="Free Shipping" class="milestone-badge-icon" width="25" height="25">'
  },
  { threshold: 250, discount: 10 },
  { threshold: 550, discount: 15 }
];


// Cart Progress Updater Class - Optimized for Safari
class CartProgressUpdater {
  constructor() {
    this.milestones = MILESTONES;
    this.currentCartValue = 0;
    this.currency = this.getCurrency();
    this.debounceTimer = null;
    this.updateTimer = null;
    this.isUpdating = false;
    this.lastUpdateTime = 0;
    this.minUpdateInterval = 300; // Minimum time between updates
    this.observers = []; // Track observers for cleanup
    this.boundHandlers = new Map(); // Cache bound event handlers
    this.init();
  }

  // --- helper: number vs text safely handle ---
isPercent(val) {
  return typeof val === 'number' && isFinite(val);
}
getRewardLabel(i) {
  const d = this.milestones[i]?.discount;
  if (this.isPercent(d)) return `${d}% off`;
  if (typeof d === 'string') return d.trim();   // e.g. "Free Shipping"
  return '';                                     // nothing
}
getBadgeText(i) {
  const d = this.milestones[i]?.discount;
  if (this.isPercent(d)) return `${d}%`;        // icon pe sirf % number
  if (typeof d === 'string') return d.trim();   // e.g. "Free Shipping"
  return '';                                     // nothing
}


  // Initialize the progress updater
  init() {
    this.updateProgress();
    this.observeCartChanges();
  }

  // Optimized debounced update with rate limiting
  debouncedUpdate() {
    const now = Date.now();
    
    // Rate limiting - prevent too frequent updates
    if (now - this.lastUpdateTime < this.minUpdateInterval) {
      if (this.updateTimer) {
        clearTimeout(this.updateTimer);
      }
      this.updateTimer = setTimeout(() => {
        this.performUpdate();
      }, this.minUpdateInterval - (now - this.lastUpdateTime));
      return;
    }

    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.performUpdate();
    }, 150); // Reduced debounce time
  }

  // Perform the actual update with guards
  performUpdate() {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    this.lastUpdateTime = Date.now();
    
    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(() => {
      this.updateProgress().finally(() => {
        this.isUpdating = false;
      });
    });
  }

  // Get store currency from Shopify
  getCurrency() {
    return window.Shopify?.currency?.active || 
           document.querySelector('[data-currency]')?.dataset.currency || 
           'GBP';
  }

  // Optimized cart value retrieval with caching
  async getCurrentCartValue() {
    // Try methods in order of performance (fastest first)
    
    // Method 1: Window cart object (fastest)
    if (window.cart?.items) {
      const cartValue = window.cart.items.reduce((total, item) => {
        return total + (item.original_price * item.quantity);
      }, 0) / 100;
      if (cartValue > 0) return cartValue;
    }

    // Method 2: CartJS (if available)
    if (window.CartJS?.cart?.items) {
      const cartValue = window.CartJS.cart.items.reduce((total, item) => {
        return total + (item.original_price * item.quantity);
      }, 0) / 100;
      if (cartValue > 0) return cartValue;
    }

    // Method 3: Parse from cached cart total element
    const cartTotalSelectors = [
      '[data-cart-total]',
      '.cart-total',
      '.cart__total-price',
      '.drawer__footer .totals__total-value',
      '.cart-drawer__footer .totals__total-value'
    ];

    for (const selector of cartTotalSelectors) {
      const element = document.querySelector(selector);
      if (element) {
        const totalText = element.textContent || element.innerText;
        const cartValue = this.parsePrice(totalText);
        if (cartValue > 0) return cartValue;
      }
    }

    // Method 4: Fetch from API (last resort)
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout
      
      const response = await fetch('/cart.json', {
        signal: controller.signal,
        headers: { 'Accept': 'application/json' }
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const cart = await response.json();
        return cart.items.reduce((sum, item) => {
          return sum + (item.original_price * item.quantity);
        }, 0) / 100;
      }
    } catch (error) {
      // Silently fail for timeout/abort
      if (error.name !== 'AbortError') {
        console.warn('Cart fetch error:', error);
      }
    }

    return 0;
  }

  // Optimized price parsing
  parsePrice(priceText) {
    if (!priceText) return 0;
    
    // Use regex for better performance
    const match = priceText.match(/[\d.,]+/);
    if (!match) return 0;
    
    const cleanPrice = match[0].replace(/,/g, '.');
    return parseFloat(cleanPrice) || 0;
  }

  // Optimized currency formatting
  formatCurrency(amount) {
    // Use cached currency symbols
    if (!this.currencySymbols) {
      this.currencySymbols = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'CAD': 'CA$',
        'AUD': 'A$'
      };
    }
    
    const symbol = this.currencySymbols[this.currency] || this.currency;
    return `${symbol}${Math.round(amount).toLocaleString()}`;
  }

  // Update milestone amounts using direct DOM queries
  updateMilestoneAmounts() {
    const milestoneElements = document.querySelectorAll('.milestone-amount');
    if (!milestoneElements) return;

    milestoneElements.forEach((element, index) => {
      if (this.milestones[index] && element.textContent !== this.formatCurrency(this.milestones[index].threshold)) {
        element.textContent = this.formatCurrency(this.milestones[index].threshold);
      }
    });
  }

  // Optimized progress update
  async updateProgress() {
    const progressContainer = document.querySelector('.cart-progress-container');
    if (!progressContainer) return;

    try {
      this.currentCartValue = await this.getCurrentCartValue();
      this.updateProgressElements();
    } catch (error) {
      console.warn('Progress update error:', error);
    }
  }

  // Optimized progress elements update
  updateProgressElements() {
    const progressMessage = document.querySelector('.progress-message');
    const progressFill = document.querySelector('.progress-bar-fill');
    const milestones = document.querySelectorAll('.milestone');

    if (!progressMessage || !progressFill || !milestones) return;

    // Update milestone amounts
    this.updateMilestoneAmounts();

    // Calculate milestone status
    let currentMilestone = -1;
    let nextMilestone = 0;
    
    for (let i = 0; i < this.milestones.length; i++) {
      if (this.currentCartValue >= this.milestones[i].threshold) {
        currentMilestone = i;
      } else {
        nextMilestone = i;
        break;
      }
    }

    // Update progress message (only if changed)
    const newMessage = this.getProgressMessage(currentMilestone, nextMilestone);
    if (progressMessage.innerHTML !== newMessage) {
      progressMessage.innerHTML = newMessage;
    }

    // Update progress bar fill
    const maxThreshold = this.milestones[this.milestones.length - 1].threshold;
    const progressPercentage = Math.min((this.currentCartValue / maxThreshold) * 100, 100);
    const newWidth = `${progressPercentage}%`;
    if (progressFill.style.width !== newWidth) {
      console.log(progressFill.style.width);
      progressFill.style.width = newWidth;
    }

    // Update milestone icons (batch DOM updates)
    const updates = [];
    milestones.forEach((milestone, index) => {
      const icon = milestone.querySelector('.milestone-icon');
      if (!icon) return;
      
      let newStyle, newContent;
      
      if (index <= currentMilestone) {
        newStyle = { background: '#28a745', color: 'white' };
        newContent = '✓';
      } else if (index === nextMilestone) {
        newStyle = { background: '#ffd700', color: '#333' };
        // NEW:
newContent = this.getBadgeText(index);

      } else {
        newStyle = { background: '#e0e0e0', color: '#666' };
        // NEW:
newContent = this.getBadgeText(index);

      }
      
      updates.push({ icon, style: newStyle, content: newContent });
    });

    // Apply all updates at once
    updates.forEach(({ icon, style, content }) => {
      if (icon.style.background !== style.background) {
        Object.assign(icon.style, style);
      }
      if (icon.innerHTML !== content) {
        icon.innerHTML = content;
      }
    });
  }

getProgressMessage(currentMilestone, nextMilestone) {
  const lastIndex = this.milestones.length - 1;

  // Helper: force text label (no HTML), with fallback
  const forceTextLabel = (index, fallbackText) => {
    let label = this.getRewardLabel(index); // may be '', plain text, or HTML
    // If empty OR contains any HTML tag, use fallback text
    if (!label || /</.test(label)) label = fallbackText;
    return label;
  };

  // Fully unlocked
  if (currentMilestone === lastIndex) {
    // If last unlocked is the first milestone (rare), still show text
    const label = forceTextLabel(currentMilestone, currentMilestone === 0 ? 'Free Shipping' : `${this.milestones[currentMilestone].discount}% off`);
    return label
      ? `🎉 Congratulations! You've unlocked <strong>${label}</strong>`
      : `🎉 Congratulations!`;
  }

  // Still progressing
  const nextThreshold = this.milestones[nextMilestone].threshold;
  const amountNeeded = Math.max(0, nextThreshold - this.currentCartValue);

  // 👉 If next is the first milestone (index 0), force text "Free Shipping"
  const label = nextMilestone === 0
    ? forceTextLabel(0, 'Free Shipping')
    : forceTextLabel(nextMilestone, `${this.milestones[nextMilestone].discount}% off`);

  if (amountNeeded > 0) {
    return label
      ? `Spend <strong>${this.formatCurrency(amountNeeded)}</strong> more for <strong>${label}</strong>`
      : `Spend <strong>${this.formatCurrency(amountNeeded)}</strong> more`;
  } else {
    return label
      ? `🎉 Congratulations! You've unlocked <strong>${label}</strong>`
      : `🎉 Congratulations!`;
  }
}



  // Optimized event observation with cleanup
  observeCartChanges() {
    // Cleanup existing observers
    this.cleanup();

    // Method 1: Cart events (optimized list)
    const cartEvents = [
      'cart:updated',
      'cart:build',
      'cart:change',
      'cart:refresh'
    ];

    const cartEventHandler = this.getOrCreateHandler('cartEvent', () => {
      this.debouncedUpdate();
    });

    cartEvents.forEach(event => {
      document.addEventListener(event, cartEventHandler, { passive: true });
    });

    // Method 2: Shopify section events
    const sectionHandler = this.getOrCreateHandler('section', () => {
      this.debouncedUpdate();
    });
    
    document.addEventListener('shopify:section:load', sectionHandler, { passive: true });

    // Method 3: Optimized mutation observer
    this.setupMutationObserver();

    // Method 4: Form events (more efficient)
    this.setupFormEvents();

    // Method 5: Optimized fetch interception
    this.setupFetchInterception();
  }

  // Get or create cached event handler
  getOrCreateHandler(key, handler) {
    if (!this.boundHandlers.has(key)) {
      this.boundHandlers.set(key, handler);
    }
    return this.boundHandlers.get(key);
  }

  // Setup mutation observer with optimizations
  setupMutationObserver() {
    const cartSelectors = [
      '.cart-drawer',
      '[data-cart-drawer]',
      '.cart__items',
      '.cart-drawer__items'
    ];

    const observerHandler = this.getOrCreateHandler('mutation', (mutations) => {
      let shouldUpdate = false;
      
      for (const mutation of mutations) {
        if (mutation.type === 'childList' || 
            (mutation.type === 'attributes' && 
             ['data-cart-total', 'data-cart-count'].includes(mutation.attributeName))) {
          shouldUpdate = true;
          break;
        }
      }
      
      if (shouldUpdate) {
        this.debouncedUpdate();
      }
    });

    cartSelectors.forEach(selector => {
      const cartContainer = document.querySelector(selector);
      if (cartContainer) {
        const observer = new MutationObserver(observerHandler);
        observer.observe(cartContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-cart-total', 'data-cart-count']
        });
        this.observers.push(observer);
      }
    });
  }

  // Setup form events efficiently
  setupFormEvents() {
    const changeHandler = this.getOrCreateHandler('change', (e) => {
      if (e.target.matches('[data-cart-quantity], .cart-quantity, input[name*="quantity"]')) {
        this.debouncedUpdate();
      }
    });

    const clickHandler = this.getOrCreateHandler('click', (e) => {
      if (e.target.matches('[data-cart-remove], .cart-remove, [data-add-to-cart], .add-to-cart')) {
        setTimeout(() => this.debouncedUpdate(), e.target.matches('[data-cart-remove], .cart-remove') ? 300 : 800);
      }
    });

    document.addEventListener('change', changeHandler, { passive: true });
    document.addEventListener('click', clickHandler, { passive: true });
  }

  // Setup fetch interception
  setupFetchInterception() {
    if (this.originalFetch) return; // Already setup

    this.originalFetch = window.fetch;
    const self = this;
    
    window.fetch = function(...args) {
      const url = args[0];
      const isCartRequest = typeof url === 'string' && url.includes('/cart');
      
      if (isCartRequest) {
        return self.originalFetch.apply(this, args).then(response => {
          setTimeout(() => self.debouncedUpdate(), 200);
          return response;
        });
      }
      
      return self.originalFetch.apply(this, args);
    };
  }

  // Cleanup method
  cleanup() {
    // Clear timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }

    // Disconnect observers
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];

    // Restore original fetch
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }
  }

  // Destroy method for cleanup
  destroy() {
    this.cleanup();
    this.boundHandlers.clear();
  }
}

// Optimized initialization
function initCartProgress() {
  // Check if already initialized
  if (window.cartProgressUpdater) {
    return;
  }

  const init = () => {
    const progressContainer = document.querySelector('.cart-progress-container');
    if (progressContainer && !window.cartProgressUpdater) {
      window.cartProgressUpdater = new CartProgressUpdater();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    // Use setTimeout to avoid blocking
    setTimeout(init, 100);
  }
}

// Auto-initialize
initCartProgress();

// Cart drawer toggle optimization
document.addEventListener('click', (e) => {
  if (e.target.matches('[data-cart-drawer-toggle], .cart-drawer-toggle, .cart-toggle')) {
    setTimeout(() => {
      const progressContainer = document.querySelector('.cart-progress-container');
      if (progressContainer && !window.cartProgressUpdater) {
        window.cartProgressUpdater = new CartProgressUpdater();
      }
    }, 500);
  }
}, { passive: true });

// Export for manual use
window.CartProgressUpdater = CartProgressUpdater;

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (window.cartProgressUpdater) {
    window.cartProgressUpdater.destroy();
  }
});